import type { SupabaseClient } from '../supabase/client';
import type {
  BillingErrorKey,
  BillingAccessMode,
  EntitlementBlockReason,
  EntitlementGate,
  EntitlementGateResult,
  BillingPlanCatalogItem,
  BillingSummary,
  BillingUsagePeriod,
  ChangeSubscriptionInput,
  ChangeSubscriptionResult,
  ConfirmRevisionInput,
  ConfirmSubscriptionInput,
  EntitlementSnapshot,
  PilotAccessGrant,
  StartSubscriptionInput,
  StartSubscriptionResult,
  StoreBillingSubscription,
} from '../types/billing';

export interface BillingService {
  getBillingSummary(client: SupabaseClient, storeId: string): Promise<BillingSummary>;
  getEntitlementSnapshot(client: SupabaseClient, storeId: string): Promise<EntitlementSnapshot>;
  getBillingGateResult(
    client: SupabaseClient,
    storeId: string,
    gate: EntitlementGate,
  ): Promise<EntitlementGateResult>;
  startSubscription(
    client: SupabaseClient,
    input: StartSubscriptionInput,
  ): Promise<StartSubscriptionResult>;
  confirmSubscription(client: SupabaseClient, input: ConfirmSubscriptionInput): Promise<void>;
  cancelSubscription(client: SupabaseClient, storeId: string): Promise<void>;
  changeSubscriptionPlan(
    client: SupabaseClient,
    input: ChangeSubscriptionInput,
  ): Promise<ChangeSubscriptionResult>;
  confirmRevision(client: SupabaseClient, input: ConfirmRevisionInput): Promise<void>;
  retryPayment(client: SupabaseClient, storeId: string): Promise<void>;
  mapBillingError(error: unknown): BillingErrorKey;
}

type JsonRecord = Record<string, unknown>;

const BILLING_REASON_ERROR_KEYS: Record<EntitlementBlockReason, BillingErrorKey> = {
  missing_subscription: 'admin.entitlements.reasons.missingSubscription',
  configuration_error: 'admin.entitlements.reasons.configurationError',
  activation_pending: 'admin.entitlements.reasons.activationPending',
  payment_required: 'admin.entitlements.reasons.paymentRequired',
  suspended: 'admin.entitlements.reasons.suspended',
  expired: 'admin.entitlements.reasons.expired',
  cancelled: 'admin.entitlements.reasons.cancelled',
  product_limit_reached: 'admin.entitlements.reasons.productLimitReached',
  storage_limit_reached: 'admin.entitlements.reasons.storageLimitReached',
  ai_quota_exhausted: 'admin.entitlements.reasons.aiQuotaExhausted',
  plan_upgrade_required: 'admin.entitlements.reasons.planUpgradeRequired',
  permission_denied: 'admin.entitlements.reasons.permissionDenied',
  owner_required: 'admin.entitlements.reasons.ownerRequired',
  paid_basis_missing: 'admin.entitlements.reasons.paidBasisMissing',
  environment_mismatch: 'admin.entitlements.reasons.environmentMismatch',
};

const BILLING_ERROR_KEYS = new Set<BillingErrorKey>([
  'admin.billing.errors.permissionDenied',
  'admin.billing.errors.paypalUnavailable',
  'admin.billing.errors.configurationMissing',
  'admin.billing.errors.activationPending',
  'admin.billing.errors.planMismatch',
  'admin.billing.errors.planAlreadyActive',
  'admin.billing.errors.reconsentRequired',
  'admin.billing.errors.webhookUnverified',
  'admin.billing.errors.paymentPaused',
  'admin.billing.errors.retry',
  ...Object.values(BILLING_REASON_ERROR_KEYS),
  'common.errors.networkFailed',
  'common.errors.unknown',
]);

function record(value: unknown): JsonRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Billing RPC returned invalid payload');
  }
  return value as JsonRecord;
}

function maybeRecord(value: unknown): JsonRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function bool(value: unknown): boolean {
  return value === true;
}

function billingErrorKey(value: unknown): BillingErrorKey | null {
  return typeof value === 'string' && BILLING_ERROR_KEYS.has(value as BillingErrorKey)
    ? (value as BillingErrorKey)
    : null;
}

function billingBlockReason(value: unknown): EntitlementBlockReason | null {
  return typeof value === 'string' && value in BILLING_REASON_ERROR_KEYS
    ? (value as EntitlementBlockReason)
    : null;
}

async function readFunctionErrorPayload(error: unknown): Promise<JsonRecord | null> {
  const context = maybeRecord(error)?.context;
  if (context instanceof Response) {
    try {
      return maybeRecord(await context.clone().json());
    } catch {
      return null;
    }
  }
  return maybeRecord(context);
}

async function normalizeBillingFunctionError(error: unknown): Promise<unknown> {
  const payload = await readFunctionErrorPayload(error);
  if (!payload) return error;
  const source = maybeRecord(error);
  return {
    ...(source ?? {}),
    ...payload,
    messageKey: billingErrorKey(payload.messageKey) ?? payload.messageKey,
    reason: billingBlockReason(payload.reason) ?? payload.reason,
  };
}

function plan(row: unknown): BillingPlanCatalogItem {
  const value = record(row);
  return {
    id: text(value.id) ?? '',
    planKey: (text(value.planKey) ??
      text(value.plan_key) ??
      'starter') as BillingPlanCatalogItem['planKey'],
    interval: (text(value.interval) ??
      text(value.billing_interval) ??
      'monthly') as BillingPlanCatalogItem['interval'],
    currency: 'ILS',
    amountAgorot: num(value.amountAgorot ?? value.amount_agorot),
    paypalPlanId: text(value.paypalPlanId ?? value.paypal_plan_id) ?? '',
    paypalEnvironment: (text(value.paypalEnvironment ?? value.paypal_environment) ??
      'sandbox') as BillingPlanCatalogItem['paypalEnvironment'],
    activeProductLimit: num(value.activeProductLimit ?? value.active_product_limit),
    mediaStorageBytes: num(value.mediaStorageBytes ?? value.media_storage_bytes),
    aiImageEditsPerPeriod: num(value.aiImageEditsPerPeriod ?? value.ai_image_edits_per_period),
    promotionToolsLevel: (text(value.promotionToolsLevel ?? value.promotion_tools_level) ??
      'basic_store_link') as BillingPlanCatalogItem['promotionToolsLevel'],
    analyticsLevel: (text(value.analyticsLevel ?? value.analytics_level) ??
      'basic') as BillingPlanCatalogItem['analyticsLevel'],
    supportLevel: (text(value.supportLevel ?? value.support_level) ??
      'standard') as BillingPlanCatalogItem['supportLevel'],
  };
}

function subscription(value: unknown): StoreBillingSubscription | null {
  if (value === null || value === undefined) return null;
  const row = record(value);
  return {
    id: text(row.id) ?? '',
    storeId: text(row.storeId ?? row.store_id) ?? '',
    state: (text(row.state) ?? 'missing') as StoreBillingSubscription['state'],
    planKey: text(row.planKey ?? row.plan_key) as StoreBillingSubscription['planKey'],
    interval: text(row.interval ?? row.billing_interval) as StoreBillingSubscription['interval'],
    currency: text(row.currency) as StoreBillingSubscription['currency'],
    paypalEnvironment: text(
      row.paypalEnvironment ?? row.paypal_environment,
    ) as StoreBillingSubscription['paypalEnvironment'],
    paypalSubscriptionId: text(row.paypalSubscriptionId ?? row.paypal_subscription_id),
    paypalPlanId: text(row.paypalPlanId ?? row.paypal_plan_id),
    paypalStatus: text(row.paypalStatus ?? row.paypal_status),
    currentPeriodStart: text(row.currentPeriodStart ?? row.current_period_start),
    currentPeriodEnd: text(row.currentPeriodEnd ?? row.current_period_end),
    paidThroughAt: text(row.paidThroughAt ?? row.paid_through_at),
    latestPaidBasisId: text(row.latestPaidBasisId ?? row.latest_paid_basis_id),
    cancelAtPeriodEnd: bool(row.cancelAtPeriodEnd ?? row.cancel_at_period_end),
    graceEndsAt: text(row.graceEndsAt ?? row.grace_ends_at),
    pendingPlanKey: text(
      row.pendingPlanKey ?? row.pending_plan_key,
    ) as StoreBillingSubscription['pendingPlanKey'],
    pendingInterval: text(
      row.pendingInterval ?? row.pending_billing_interval,
    ) as StoreBillingSubscription['pendingInterval'],
    pendingEffectiveAt: text(row.pendingEffectiveAt ?? row.pending_effective_at),
    pendingRevisionIntentId: text(row.pendingRevisionIntentId ?? row.pending_revision_intent_id),
    lastVerifiedPayPalEventAt: text(
      row.lastVerifiedPayPalEventAt ?? row.last_verified_paypal_event_at,
    ),
    lastPayPalSyncAt: text(row.lastPayPalSyncAt ?? row.last_paypal_sync_at),
  };
}

function usage(value: unknown): BillingUsagePeriod | null {
  if (value === null || value === undefined) return null;
  const row = record(value);
  return {
    id: text(row.id) ?? '',
    storeId: text(row.storeId ?? row.store_id) ?? '',
    subscriptionId: text(row.subscriptionId ?? row.subscription_id),
    periodStart: text(row.periodStart ?? row.period_start) ?? '',
    periodEnd: text(row.periodEnd ?? row.period_end) ?? '',
    aiImageEditsUsed: num(row.aiImageEditsUsed ?? row.ai_image_edits_used),
  };
}

function pilotGrant(value: unknown): PilotAccessGrant | null {
  if (value === null || value === undefined) return null;
  const row = record(value);
  return {
    id: text(row.id) ?? '',
    storeId: text(row.storeId ?? row.store_id) ?? '',
    planKey: 'pro',
    accessStartsAt: text(row.accessStartsAt ?? row.access_starts_at) ?? '',
    accessEndsAt: text(row.accessEndsAt ?? row.access_ends_at) ?? '',
    revokedAt: text(row.revokedAt ?? row.revoked_at),
  };
}

function entitlement(value: unknown): EntitlementSnapshot {
  const row = record(value);
  const rawOverages = Array.isArray(row.overages) ? row.overages : [];
  const hasVerifiedPaidBasis = bool(row.hasVerifiedPaidBasis);
  const hasActivePilotAccess = bool(row.hasActivePilotAccess);
  return {
    storeId: text(row.storeId) ?? '',
    state: (text(row.state) ?? 'missing') as EntitlementSnapshot['state'],
    planKey: text(row.planKey) as EntitlementSnapshot['planKey'],
    interval: text(row.interval) as EntitlementSnapshot['interval'],
    paidAccessUntil: text(row.paidAccessUntil),
    graceEndsAt: text(row.graceEndsAt),
    hasVerifiedPaidBasis,
    hasActivePilotAccess,
    accessSource: (text(row.accessSource) ??
      (hasActivePilotAccess
        ? 'pilot'
        : hasVerifiedPaidBasis
          ? 'paypal'
          : 'none')) as EntitlementSnapshot['accessSource'],
    pilotAccessUntil: text(row.pilotAccessUntil),
    canUsePaidActions: bool(row.canUsePaidActions),
    activeProductLimit: num(row.activeProductLimit),
    activeProductsUsed: num(row.activeProductsUsed),
    mediaStorageBytes: num(row.mediaStorageBytes),
    mediaStorageBytesUsed: num(row.mediaStorageBytesUsed),
    aiImageEditsPerPeriod: num(row.aiImageEditsPerPeriod),
    aiImageEditsUsed: num(row.aiImageEditsUsed),
    promotionToolsLevel: text(
      row.promotionToolsLevel,
    ) as EntitlementSnapshot['promotionToolsLevel'],
    analyticsLevel: text(row.analyticsLevel) as EntitlementSnapshot['analyticsLevel'],
    supportLevel: text(row.supportLevel) as EntitlementSnapshot['supportLevel'],
    overages: rawOverages.flatMap((item) => {
      const overage = record(item);
      const gate = text(overage.gate);
      if (gate !== 'active_products' && gate !== 'media_storage' && gate !== 'ai_image_edits') {
        return [];
      }
      return { gate, used: num(overage.used), limit: num(overage.limit) };
    }),
  };
}

function gateResult(value: unknown): EntitlementGateResult {
  const row = record(value);
  return {
    allowed: bool(row.allowed),
    reason: text(row.reason) as EntitlementGateResult['reason'],
    messageKey: text(row.messageKey ?? row.message_key) as EntitlementGateResult['messageKey'],
    upgradeTargetPlanKey: text(
      row.upgradeTargetPlanKey ?? row.upgrade_target_plan_key,
    ) as EntitlementGateResult['upgradeTargetPlanKey'],
  };
}

export async function getBillingSummary(
  client: SupabaseClient,
  storeId: string,
): Promise<BillingSummary> {
  const { data, error } = await client.rpc('get_admin_billing_summary', {
    target_store_id: storeId,
  });
  if (error) throw error;
  const payload = record(data);
  return {
    catalog: Array.isArray(payload.catalog) ? payload.catalog.map(plan) : [],
    subscription: subscription(payload.subscription),
    pilotGrant: pilotGrant(payload.pilotGrant ?? payload.pilot_grant),
    accessMode: (text(payload.accessMode ?? payload.access_mode) ?? 'paypal') as BillingAccessMode,
    usage: usage(payload.usage),
    entitlement: entitlement(payload.entitlement),
    isOwner: bool(payload.isOwner),
    paypalClientIdConfigured: bool(payload.paypalClientIdConfigured),
    serverConfigured: bool(payload.serverConfigured),
  };
}

export async function getEntitlementSnapshot(
  client: SupabaseClient,
  storeId: string,
): Promise<EntitlementSnapshot> {
  const { data, error } = await client.rpc('get_store_entitlement_snapshot', {
    target_store_id: storeId,
  });
  if (error) throw error;
  return entitlement(data);
}

export async function getBillingGateResult(
  client: SupabaseClient,
  storeId: string,
  gate: EntitlementGate,
): Promise<EntitlementGateResult> {
  const { data, error } = await client.rpc('billing_gate_result', {
    target_store_id: storeId,
    target_gate: gate,
  });
  if (error) throw error;
  return gateResult(data);
}

async function invokeBillingFunction<T>(
  client: SupabaseClient,
  body: JsonRecord,
  parse: (value: unknown) => T,
): Promise<T> {
  const { data, error } = await client.functions.invoke('paypal-subscriptions', { body });
  if (error) throw await normalizeBillingFunctionError(error);
  return parse(data);
}

export function startSubscription(
  client: SupabaseClient,
  input: StartSubscriptionInput,
): Promise<StartSubscriptionResult> {
  return invokeBillingFunction(client, { action: 'start', ...input }, (value) => {
    const payload = record(value);
    return {
      intentId: text(payload.intentId) ?? '',
      paypalPlanId: text(payload.paypalPlanId) ?? '',
      paypalEnvironment: (text(payload.paypalEnvironment) ??
        'sandbox') as StartSubscriptionResult['paypalEnvironment'],
      expiresAt: text(payload.expiresAt) ?? '',
      customId: text(payload.customId) ?? '',
    };
  });
}

export async function confirmSubscription(
  client: SupabaseClient,
  input: ConfirmSubscriptionInput,
): Promise<void> {
  await invokeBillingFunction(client, { action: 'confirm', ...input }, () => undefined);
}

export async function cancelSubscription(client: SupabaseClient, storeId: string): Promise<void> {
  await invokeBillingFunction(client, { action: 'cancel', storeId }, () => undefined);
}

export function changeSubscriptionPlan(
  client: SupabaseClient,
  input: ChangeSubscriptionInput,
): Promise<ChangeSubscriptionResult> {
  return invokeBillingFunction(client, { action: 'change_plan', ...input }, (value) => {
    const payload = record(value);
    return {
      revisionIntentId: text(payload.revisionIntentId) ?? '',
      approvalUrl: text(payload.approvalUrl) ?? '',
      pendingEffectiveAt: text(payload.pendingEffectiveAt),
    };
  });
}

export async function confirmRevision(
  client: SupabaseClient,
  input: ConfirmRevisionInput,
): Promise<void> {
  await invokeBillingFunction(client, { action: 'confirm_revision', ...input }, () => undefined);
}

export async function retryPayment(client: SupabaseClient, storeId: string): Promise<void> {
  await invokeBillingFunction(client, { action: 'retry_payment', storeId }, () => undefined);
}

export function mapBillingError(error: unknown): BillingErrorKey {
  const payload = maybeRecord(error);
  const explicitMessageKey = billingErrorKey(payload?.messageKey);
  if (explicitMessageKey) return explicitMessageKey;

  const reason = billingBlockReason(payload?.reason);
  if (reason) return BILLING_REASON_ERROR_KEYS[reason];

  const code = payload && typeof payload.code === 'string' ? payload.code : null;
  if (code === '42501') return 'admin.billing.errors.permissionDenied';
  if (code === 'P0601') return 'admin.entitlements.reasons.paymentRequired';
  if (code === 'P0602') return 'admin.entitlements.reasons.storageLimitReached';
  if (code === 'P0604') return 'admin.entitlements.reasons.aiQuotaExhausted';
  if (code === 'P0606') return 'admin.entitlements.reasons.productLimitReached';
  if (code === 'P0608') return 'admin.billing.errors.planMismatch';
  if (code === 'P0609') return 'admin.billing.errors.activationPending';
  if (code === 'P0611') return 'admin.entitlements.reasons.paidBasisMissing';
  if (code === 'P0610') return 'admin.billing.errors.retry';
  if (code === 'P0613') return 'admin.entitlements.reasons.environmentMismatch';
  if (code === 'P0614') return 'admin.billing.errors.planAlreadyActive';
  if (code === 'P0615') return 'admin.billing.errors.paymentPaused';
  if (code === 'FunctionsFetchError' || code === 'NETWORK_ERROR')
    return 'common.errors.networkFailed';
  return 'common.errors.unknown';
}

export const billingService: BillingService = {
  getBillingSummary,
  getBillingGateResult,
  getEntitlementSnapshot,
  startSubscription,
  confirmSubscription,
  cancelSubscription,
  changeSubscriptionPlan,
  confirmRevision,
  retryPayment,
  mapBillingError,
};
